import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { MessageInput } from '@/messages/MessageInput';
import { MessageSendError } from '@/messages/MessageSendError';
import { MessagesSidebarLayout } from '@/messages/MessagesSidebarLayout';
import { MessagesContainer } from '@/messages/messagesStyles';
import { useSelfOnlyMessages } from '@/messages/useSelfOnlyMessages';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/messages/compose',
)({
  component: MessagesComposeRoute,
  /**
   * Only a positive integer survives, so an unparseable `?r=` is treated like
   * an absent one (no composer) rather than passing for a recipient.
   */
  validateSearch: (search): { r?: number } => {
    const parsed = Number(search.r);
    return Number.isInteger(parsed) && parsed > 0 ? { r: parsed } : {};
  },
});

function MessagesComposeRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const navigate = useNavigate();
  const { r: recipientId } = Route.useSearch();
  const wrongMember = useSelfOnlyMessages(memberId);
  const [failure, setFailure] = useState<unknown>(null);

  /**
   * Does `?r=` name a real person? Legacy renders nothing to type into
   * otherwise.
   *
   * `get_member_profile` answers 404 for a missing member and for a deleted
   * user, so one call answers both. A private member is a 200 with no sections
   * and the composer renders: who may be messaged is the send gate's call.
   */
  const { data: recipient } = api.getMemberProfile.useQuery(
    { path: { member_id: recipientId ?? 0 } },
    {
      enabled: recipientId !== undefined,
      // A member id that does not resolve is a real answer, not a blip.
      retry: (failureCount: number, err: unknown) =>
        failureCount < 3 && !isErrorCode(err, 'resource_not_found'),
    },
  );

  const { mutateAsync: createThread } = api.createThread.useMutation();

  const handleSendMessage = async (message: string) => {
    if (recipientId === undefined) return;
    setFailure(null);

    let threadId: number;
    try {
      const response = await createThread({
        // Find-or-create the 1:1 thread with this recipient, then jump into it.
        body: { recipient_id: recipientId, content: message },
      });
      threadId = response.thread_id;
    } catch (error) {
      setFailure(error);
      // Rethrown so MessageInput keeps the draft — it clears only on resolve.
      throw error;
    }

    await api.listMessageThreads.invalidateQueries();
    navigate({
      to: '/members/$memberId/messages/view/$threadId/',
      params: { memberId, threadId: `${threadId}` },
    });
  };

  if (wrongMember) return null;

  const canCompose = recipientId !== undefined && recipient !== undefined;

  return (
    <MessagesSidebarLayout
      onThreadClick={(thread) =>
        navigate({
          to: '/members/$memberId/messages/view/$threadId/',
          params: { memberId, threadId: `${thread.id}` },
        })
      }
    >
      <ComposeHeader>
        <ComposeHeaderTop>{t('wbs.new-message')}</ComposeHeaderTop>
      </ComposeHeader>

      <MessagesContainer />

      {failure !== null && <MessageSendError error={failure} />}

      {canCompose && (
        <MessageInput onSend={handleSendMessage} buttonLabel={t('wbs.send')} />
      )}
    </MessagesSidebarLayout>
  );
}

const ComposeHeader = styled.div`
  border-bottom: 1px solid var(--grey-300);
  display: flex;
  flex-direction: column;
`;

const ComposeHeaderTop = styled.div`
  border-bottom: 1px solid var(--grey-300);
  color: var(--black);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 500;
  padding: 20px 20px;
`;
